import toast from 'react-hot-toast';
import { extractErrorMessage } from './api';

/**
 * Helpers toast unifiés (US-TR-03) — utiliser depuis tout code applicatif
 * au lieu de manipuler des banners locaux. Le `<Toaster>` global est monté
 * dans `App.tsx`.
 *
 * Patterns :
 *   import { showSuccess, showError } from '../lib/toast';
 *   showSuccess('Bâtiment créé');
 *   try { ... } catch (err) { showError(err); }
 *
 * Pour les promesses asynchrones, `showLoading(...)` retourne un toast id
 * que le caller peut mettre à jour avec `toast.success(msg, { id })`.
 */

export function showSuccess(message: string): string {
  return toast.success(message);
}

export function showError(messageOrError: unknown): string {
  const msg =
    typeof messageOrError === 'string' ? messageOrError : extractErrorMessage(messageOrError);
  return toast.error(msg);
}

export function showInfo(message: string): string {
  return toast(message, { icon: 'ℹ️' });
}

export function showLoading(message: string): string {
  return toast.loading(message);
}

/**
 * Wrapper utilitaire pour les actions async : affiche `loading` → puis
 * `success` ou `error` selon le résultat. Réutilise `extractErrorMessage`
 * pour formater proprement les erreurs Axios.
 */
export async function withToast<T>(
  promise: Promise<T>,
  messages: { loading: string; success: string; error?: string },
): Promise<T> {
  return toast.promise(promise, {
    loading: messages.loading,
    success: messages.success,
    error: (err) => messages.error ?? extractErrorMessage(err),
  });
}

export { toast };
